/**
 * Comparação de duas projeções da mesma fila (antes × depois de uma mudança):
 * quem passa a ser empurrado e como a ocupação de cada período muda. É a
 * régua comum de "Simular venda" e do "Cenário do PCP", para que os dois
 * digam a mesma coisa sobre o mesmo cliente.
 */
import { somarDu } from "./calendario";
import { CAMADA } from "./classificacao";
import { cargaDoItem, diaDaPosicao, ocupacaoDoPeriodo } from "./fila";
import { ESCALA, QUANTIDADE_PADRAO, RELACAO, listarPeriodos } from "./periodos";

// Teto de períodos olhados para frente (2 anos), para uma data muito
// distante não gerar uma lista sem fim.
const LIMITE_SEGUINTES = Object.freeze({ [ESCALA.MES]: 24, [ESCALA.QUINZENA]: 48, [ESCALA.SEMANA]: 104 });

// A venda simulada é a própria mudança: não "empurra a si mesma". A venda
// assinada fica de fora da planilha mas é cliente de verdade, então conta.
const FORA_DA_PLANILHA = new Set([CAMADA.VENDA_SIMULADA]);

/** Chave estável de um item entre duas projeções. */
export function chaveDoItem(item) {
  return item.identidade || item.chave || `linha:${item.linha_planilha}`;
}

/**
 * Último dia útil em que a marcenaria pode terminar para a entrega sair no
 * dia: `G` dias úteis antes da entrega (G = folga término → carregamento).
 */
export function limiteDaMarcenaria(entrega, G, naoUteis) {
  return G > 0 ? somarDu(entrega, -G, naoUteis) : entrega;
}

function diaNoFator(projecao, item, fator) {
  if (fator === 1) return item.diaProjetado;
  return diaDaPosicao({
    posicao: cargaDoItem(item),
    ritmo: projecao.ritmo,
    fator,
    hoje: projecao.hoje,
    naoUteis: projecao.naoUteis,
  });
}

/**
 * Clientes (pedido + cliente) que passam a estourar o limite da marcenaria
 * DEPOIS da mudança e não estouravam antes. `fator` 1 = projeção central;
 * `1 + erroMax` = pior caso da faixa de precisão.
 * `ignorar` = chaves dos itens que a própria mudança moveu (o pedido
 * reprogramado não "empurra a si mesmo").
 * Retorna `[{pedido, cliente, entrega, antes, depois, ambientes}]` em ordem
 * de entrega; `antes`/`depois` = dia projetado do ambiente mais afetado.
 */
export function clientesEmpurrados({ antes, depois, G = 0, fator = 1, ignorar = new Set() }) {
  if (!antes?.ritmo || !depois?.ritmo) return [];
  const diaAntes = new Map((antes.itens || []).map((item) => [chaveDoItem(item), diaNoFator(antes, item, fator)]));
  const grupos = new Map();
  (depois.itens || []).forEach((item) => {
    const chave = chaveDoItem(item);
    if (FORA_DA_PLANILHA.has(item.camada) || ignorar.has(chave) || !item.entrega) return;
    const limite = limiteDaMarcenaria(item.entrega, G, depois.naoUteis);
    const dia = diaNoFator(depois, item, fator);
    const anterior = diaAntes.get(chave);
    if (!(dia > limite) || !anterior || anterior > limite) return;
    // Venda assinada (sem pedido ainda) também tem prazo prometido ao cliente:
    // pode ser empurrada. Agrupa pela venda, como em `clientesDoPeriodo`.
    const grupoChave = `${item.pedido || ""}|${item.cliente || ""}|${item.vendaAssinadaId || ""}`;
    const grupo = grupos.get(grupoChave) || {
      pedido: item.pedido,
      cliente: item.cliente,
      ...(item.vendaAssinadaId ? { vendaAssinadaId: item.vendaAssinadaId } : {}),
      ambientes: 0,
      pior: null,
    };
    grupo.ambientes += 1;
    if (!grupo.pior || dia > grupo.pior.depois) grupo.pior = { entrega: item.entrega, antes: anterior, depois: dia };
    grupos.set(grupoChave, grupo);
  });
  return [...grupos.values()]
    .map(({ pior, ...grupo }) => ({ ...grupo, ...pior }))
    .sort((a, b) => (a.entrega < b.entrega ? -1 : a.entrega > b.entrega ? 1 : 0));
}

/**
 * Períodos de `hoje` em diante até cobrir `ate` (no mínimo os da tela).
 * A escala Dia não tem ocupação: usa o mês.
 */
export function periodosAte(escala, hoje, ate) {
  const escalaComOcupacao = escala === ESCALA.DIA || !LIMITE_SEGUINTES[escala] ? ESCALA.MES : escala;
  const minimo = QUANTIDADE_PADRAO[escalaComOcupacao].seguintes + 1;
  return listarPeriodos(escalaComOcupacao, hoje, { seguintes: LIMITE_SEGUINTES[escalaComOcupacao], anteriores: 0 })
    .filter((p) => p.relacao !== RELACAO.PASSADO)
    .filter((p, i) => i < minimo || (ate && p.inicio <= ate));
}

/** `[{periodo, antes, depois}]`, cada lado = `ocupacaoDoPeriodo`. */
export function ocupacaoAntesDepois({ antes, depois, periodos }) {
  return (periodos || []).map((periodo) => ({
    periodo,
    antes: ocupacaoDoPeriodo(antes, periodo),
    depois: ocupacaoDoPeriodo(depois, periodo),
  }));
}
